import React from 'react';

import { Path } from '~/paths';
import { ChapterWrapper } from '~/ChapterWrapper';
import Footer from '~/Footer';
import { ELink } from '~/ExternalLink';


const correlationGrids = require('../assets/images/correlation_grids.png');
const correlationExamples = require('../assets/images/correlation_examples.png');


export const CorrelationMeta = {
    title: "Korelace",
    shortTitle: "Korelace",
    path: Path.correlation,
    element: (sectionNumber:string) => <Correlation sectionNumber={sectionNumber}/>,
    sectionNumber: "",
}

const TOCSpec = [
    "corr-orientation",
    "corr-formula",
    "corr-examples",
    "corr-interpretation",
    "corr-affine",
];


export function Correlation({sectionNumber}: {sectionNumber: string}) {

    return (
        <ChapterWrapper sectionNumber={sectionNumber} title={CorrelationMeta.title} TOCSpec={TOCSpec}>

            Reference: <ELink href="https://en.wikipedia.org/wiki/Correlation">Wikipedie</ELink>.

            <h2 id="corr-orientation">Orientace</h2>

            <p>Dejme tomu, že máme soubor dvojrozměrných dat (např. čtyř bodů ve 2D):
            {`$$
            (x_1, y_1), (x_2, y_2), (x_3, y_3), (x_4, y_4)
            $$`}
            Můžeme samostatně určit průměry a rozptyly souboru x-ových souřadnic
            a souboru y-ových souřadnic:
            {`$$\\begin{matrix}
                \\text{data} & \\text{průměr} & \\text{sm. odchylka} \\\\
                (x_1, x_2, x_3, x_4) & \\bar{x} & \\sigma_x \\\\
                (y_1, \\, y_2, \\, y_3, \\, y_4) & \\bar{y} & \\sigma_y
            \\end{matrix}$$`}
            Obrázek níže ukazuje tři různé případy dat, které mají
            identický soubor {"$(x_1, x_2, x_3, x_4)$"} i
            identický soubor {"$(y_1, y_2, y_3, y_4)$"}, a
            liší se jen různým "spárováním" dvojic bodů.
            </p>
            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={correlationGrids} style={{width: "80%"}}/>
            </div>
            <p>
            Ve všech těchto případech vyjdou stejné hodnoty {"$\\bar{x}$"}, {"$\\bar{y}$"}, {"$\\sigma_x$"} a {"$\\sigma_y$"}.
            </p>
            <p><strong>Koeficient korelace</strong> je číslo, které dokáže reflektovat, jak jsou data "spárována".
            V prvním případě pro páry platí "čím větší x, tím větší y" (a koeficient korelace je blízko 1),
            ve druhém případě platí "čím větší x, tím menší y" (a koeficient korelace je blízko -1),
            a ve třetím případě je koeficient korelace blízko 0.
            </p>

            <h2 id="corr-formula">Koeficient korelace</h2>
            <p>Značení: {"$r$"}, nebo {"$\\rho$"}, popř. {"$r_{xy}$"} - indikujeme, že korelace je mezi
            daty {"$\\{x_i\\}_{i=1}^{n}$"} a {"$\\{y_i\\}_{i=1}^{n}$"}.
            </p>

            <p>
                Vzorec pro výpočet lze uvést v různých tvarech, a v každém z nich je možné vidět něco specifického.
            </p>

            <p>
            Tvar 1:
            {`$$
            r = \\frac{\\frac{1}{n}\\sum_{i=1}^{n} (x_i - \\bar{x})(y_i - \\bar{y})}{\\sigma_x \\sigma_y}
            $$`}
            Tvar 2 (získaný roznásobením čitatele):
            {`$$
            r = \\frac{\\frac{1}{n}\\sum_{i=1}^{n}x_iy_i - \\bar{x}\\bar{y}}{\\sigma_x \\sigma_y}
            $$`}
            Tvar 3 (je v něm vidět z-skore):
            {`$$
            r = \\frac{1}{n}\\sum_{i=1}^{n} \\left(\\frac{x_i - \\bar{x}}{\\sigma_x}\\right)\\cdot\\left(\\frac{y_i - \\bar{y}}{\\sigma_y}\\right)
            $$`}
            Tvar 4 (skalární součin):
            {`$$
            r = \\frac{\\sum_{i=1}^{n} (x_i - \\bar{x})(y_i - \\bar{y})}{\\sqrt{\\sum_{i=1}^{n} (x_i - \\bar{x})^2} \\sqrt{\\sum_{i=1}^{n} (y_i - \\bar{y})^2}}
            $$`}
            Pozn.: Tvar 4 získáme z tvaru 1 dosazením rozepsaného vzorce pro {"$\\sigma_x$"} a {"$\\sigma_y$"} a pokrácením {"$\\textcolor{BrickRed}{\\frac{1}{n}}$"}:
            {`$$\\small
            r = \\frac{\\textcolor{BrickRed}{\\frac{1}{n}}\\sum_{i=1}^{n} (x_i - \\bar{x})(y_i - \\bar{y})}{\\sqrt{\\textcolor{BrickRed}{\\frac{1}{n}}\\sum_{i=1}^{n} (x_i - \\bar{x})^2} \\sqrt{\\textcolor{BrickRed}{\\frac{1}{n}}\\sum_{i=1}^{n} (y_i - \\bar{y})^2}}
            $$`}
            Pokud označíme vektor {"$\\overrightarrow{u} = \\{x_i - \\bar{x}\\}_{i=1}^{n}$"} a
            vektor {"$\\overrightarrow{v} = \\{y_i - \\bar{y}\\}_{i=1}^{n}$"}, potom tento tvar je:
            {`$$
            r = \\frac{\\overrightarrow{u} \\cdot \\overrightarrow{v}}{\\|\\overrightarrow{u}\\| \\|\\overrightarrow{v}\\|}
            $$`}
            Jedná se evidentně o skalární součin normovaných (jednotkových) vektorů; hodnota {"$r$"} je hodnota kosinu úhlu
            mezi vektory {"$\\overrightarrow{u}$"} a {"$\\overrightarrow{v}$"}. Z tohoto vidíme, proč
            koef. korelace {"$r$"} vychází vždy v intervalu {"$\\langle -1, 1 \\rangle$"}. ☺
            </p>

            <p>Pro roztříděný soubor by tvar 1 vypadal takto:
            {`$$
            r = \\frac{\\sum_{j=1}^{m} f_j(x^*_j - \\bar{x})(y^*_j - \\bar{y})}{\\sigma_x \\sigma_y}
            $$`}
            </p>

            <h2 id="corr-examples">Příklady výpočtu</h2>
            <p></p>
            <div style={{textAlign: "center", paddingBottom: 10}}>
                <img src={correlationExamples} style={{width: "100%"}}/>
            </div>

            <h2 id="corr-interpretation">Interpretace</h2>

            <p>Pokud {"$r = 1$"}, pak všechna data leží přesně na přímce, která má kladný sklon (je rostoucí).<br/>
            Pokud {"$r = -1$"}, pak všechna data leží přesně na přímce, která má záporný sklon (je klesající).<br/>
            Pokud {"$r > 0$"}, pak data jsou kladně korelovaná, tj. převážně spíšě platí, že "čím větší x, tím větší y".<br/>
            Pokud {"$r < 0$"}, pak data jsou záporně korelovaná, tj. převážně spíšě platí, že "čím větší x, tím menší y".
            </p>
            <p>
            Viz <ELink href="https://cs.wikipedia.org/wiki/Korelace#/media/Soubor:Correlation_examples2.svg">obrázek</ELink>.
            </p>

            <p>Pozor: Různě vypadající datové soubory mohou mít stejné hodnoty průměrů a směrodatných odchylek i koeficientu korelace.
            Proto je vždy potřeba vidět i scatterplot, a nespoléhat jen na tato čísla.
            Viz <ELink href="https://en.wikipedia.org/wiki/Anscombe%27s_quartet">Anscombe's quartet</ELink>.
            </p>

            <p>Pozor: Nenulová korelace může, ale vůbec nemusí znamenat <strong>kauzalitu</strong>.
            Pokud kauzalita existuje, pak může jít objema směry - buď x způsobuje y,
            nebo y způsobuje x. Může existovat i třetí proměnná, která způsobuje obě.
            Anebo fakt, že jsou korelované, je prostě shoda okolností.
            </p>

            <p>Pozor.: Nulová korelace ne nutně znamená absenci zákonitosti,
            která by propojovala x a y. Např. pro kvadratckou
            závislost: {"$(-2, 4), (-1, 1), (0, 0), (1, 1), (2, 4)$"} bude {"$r_{xy} = 0$"}, i když
            je mezi x a y jasná závislost. Koeficient korelace měří <strong>jen lineární</strong> závislost.
            O ostatní druzích závislostí (v termilologii prof. Starka 'asociací')
            vůbec nic neříká.
            </p>

            <h2 id="corr-affine">Afinní transformace</h2>
            <p>
            Afinní transformace nemá vliv na koeficient korelace - eventuelně až na znaménko.
            </p>
            <p>Transformujme {"$u_i = kx_i + c$"}. Vím, že {"$\\bar{u} = k\\bar{x} + c$"} a {"$\\sigma_u = |k|\\sigma_x$"}.
            Počítejme koeficient korelace {"$r_{uy}$"}:
            {`$$\\begin{align}
              r_{uy} & = \\frac{\\frac{1}{n}\\sum_{i=1}^{n} (u_i - \\bar{u})(y_i - \\bar{y})}{\\sigma_u \\sigma_y} = \\\\
                     & = \\frac{\\frac{1}{n}\\sum_{i=1}^{n} (kx_i + c - (k\\bar{x} + c))(y_i - \\bar{y})}{|k|\\sigma_x \\sigma_y} = \\\\
                     & = \\frac{\\frac{1}{n}\\sum_{i=1}^{n} k(x_i - \\bar{x})(y_i - \\bar{y})}{|k|\\sigma_x \\sigma_y} = \\\\
                     & = \\frac{k}{|k|}r_{xy} = \\text{sgn}(k)r_{xy}
            \\end{align}$$`}
            Pozn.: {"$\\text{sgn}(k)$"} značí znaménko (signum) čísla {"$k$"}, a je zavedeno takto:
            {`$$
            \\text{sgn}(k) = \\begin{cases}
                \\phantom{-}1 & \\text{pro } k > 0 \\\\
                \\phantom{-}0 & \\text{pro } k = 0 \\\\
                -1 & \\text{pro } k < 0
                \\end{cases}
            $$`}
            Koeficient korelace je "symetrický vůči {"$x$"} a {"$y$"}": {"$r_{xy} = r_{yx}$"} (podívejte se na vzorec a prohoďte v něm
            {"$x$"} a {"$y$"}). Je tedy zřejmé, že afinní transformace {"$y$"} se chová stejně, a afinní tranformace obou proměnných
            současně taktéž.
            </p>

            <Footer/>
        </ChapterWrapper>
    );
}

